import { useState } from "react";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { BUDGETS, SERVICE_OPTIONS, BUSINESS_CATEGORIES, INDIAN_STATES } from "@/lib/constants";
import { SectionHeader } from "@/components/Services";
import { submitLead } from "@/services/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = {
  full_name: "",
  mobile: "",
  email: "",
  business_name: "",
  business_category: "",
  city: "",
  state: "",
  service_required: "",
  monthly_budget: "",
  message: "",
};

const isIndianMobile = (v) => /^[6-9]\d{9}$/.test(String(v).replace(/\D/g, ""));
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim());

export const LeadForm = () => {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your name";
    if (!isIndianMobile(form.mobile)) e.mobile = "Enter a valid 10-digit Indian mobile (starts 6-9)";
    if (!isEmail(form.email)) e.email = "Enter a valid email address";
    if (!form.business_name.trim()) e.business_name = "Please enter your business name";
    if (!form.business_category) e.business_category = "Select a category";
    if (!form.city.trim()) e.city = "Please enter your city";
    if (!form.state) e.state = "Select your state";
    if (!form.service_required) e.service_required = "Select a service";
    if (!form.monthly_budget) e.monthly_budget = "Select a budget";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (loading) return; // prevent duplicate submission
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        whatsapp: form.mobile,
        services_required: [form.service_required],
      };
      await submitLead(payload);
     
      if (window.fbq) {
  window.fbq("track", "Lead");
}
setDone(true);
      toast.success("Thank you! Your request has been received successfully. Our team will contact you shortly.");
      setForm(empty);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="lead-form" data-testid="lead-form-section" className="py-24 relative scroll-mt-24">
      <Toaster theme="dark" position="top-center" richColors />
      <div className="section-divider mb-24 max-w-7xl mx-auto" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-3xl overflow-hidden glass p-6 sm:p-10 lg:p-12">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#D4AF37]/20 blur-3xl pointer-events-none" />
          <div className="grid lg:grid-cols-5 gap-10 relative">
            <div className="lg:col-span-2">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3 flex items-center gap-2">
                <Sparkles size={12} /> Free Consultation
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-white leading-tight">
                Get your <span className="gold-gradient-text">FREE marketing consultation.</span>
              </h2>
              <p className="text-white/60 mt-4 leading-relaxed">
                Share a few details and our team will reach out on WhatsApp within one business day with a personalised plan for your business.
              </p>
              <ul className="mt-6 space-y-2.5">
                {["No obligation, no pressure", "100% confidential", "Custom strategy for your city & category", "Response within 24 hours"].map((li) => (
                  <li key={li} className="flex items-center gap-2 text-sm text-white/75">
                    <CheckCircle2 size={16} className="text-[#D4AF37]" /> {li}
                  </li>
                ))}
              </ul>
            </div>

            <form onSubmit={onSubmit} className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="lead-form" noValidate>
              <Field label="Full Name" error={errors.full_name} span="sm:col-span-2">
                <input data-testid="input-full-name" type="text" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Your full name" className={inputCls} required />
              </Field>

              <Field label="Mobile Number" hint="+91" error={errors.mobile}>
                <div className="flex items-stretch">
                  <span className="px-3 grid place-items-center rounded-l-lg bg-white/5 border border-white/10 border-r-0 text-white/70 text-sm">+91</span>
                  <input data-testid="input-mobile" inputMode="numeric" maxLength={10} type="tel" value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} placeholder="10-digit mobile" className={`${inputCls} rounded-l-none`} required />
                </div>
              </Field>

              <Field label="Email" error={errors.email}>
                <input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@business.com" className={inputCls} required />
              </Field>

              <Field label="Business Name" error={errors.business_name}>
                <input data-testid="input-business-name" type="text" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Your brand or business" className={inputCls} required />
              </Field>

              <Field label="Business Category" error={errors.business_category}>
                <Select value={form.business_category} onValueChange={(v) => set("business_category", v)}>
                  <SelectTrigger data-testid="select-category" className={selectCls}><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent className="bg-[#0A1128] text-white border-white/10 max-h-72">
                    {BUSINESS_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="City" error={errors.city}>
                <input data-testid="input-city" type="text" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g., Jaipur, Mumbai, Bangalore" className={inputCls} required />
              </Field>

              <Field label="State" error={errors.state}>
                <Select value={form.state} onValueChange={(v) => set("state", v)}>
                  <SelectTrigger data-testid="select-state" className={selectCls}><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent className="bg-[#0A1128] text-white border-white/10 max-h-72">
                    {INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Service Required" error={errors.service_required}>
                <Select value={form.service_required} onValueChange={(v) => set("service_required", v)}>
                  <SelectTrigger data-testid="select-service" className={selectCls}><SelectValue placeholder="Select a service" /></SelectTrigger>
                  <SelectContent className="bg-[#0A1128] text-white border-white/10 max-h-72">
                    {SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Monthly Marketing Budget" error={errors.monthly_budget}>
                <Select value={form.monthly_budget} onValueChange={(v) => set("monthly_budget", v)}>
                  <SelectTrigger data-testid="select-budget" className={selectCls}><SelectValue placeholder="Select budget in ₹" /></SelectTrigger>
                  <SelectContent className="bg-[#0A1128] text-white border-white/10">
                    {BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Message (optional)" span="sm:col-span-2">
                <textarea data-testid="input-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your goals..." className={`${inputCls} resize-none`} />
              </Field>

              <div className="sm:col-span-2 flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
                <p className="text-xs text-white/40">By submitting, you agree to be contacted on WhatsApp/phone.</p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="lead-submit-button"
                  aria-busy={loading}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#D4AF37] hover:bg-[#F3E5AB] disabled:opacity-60 disabled:cursor-not-allowed text-[#050B14] text-sm font-semibold transition-all shadow-[0_10px_40px_-10px_rgba(212,175,55,0.6)]"
                >
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Book FREE Consultation"}
                </button>
              </div>

              {done && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="sm:col-span-2 flex items-center gap-2 rounded-xl bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 px-4 py-3 text-sm" data-testid="lead-success">
                  <CheckCircle2 size={16} /> Thank you! Your request has been received successfully. Our team will contact you shortly.
                </motion.div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const inputCls = "w-full rounded-lg bg-[#0A1128]/60 border border-white/10 px-4 py-3 text-sm text-white placeholder-white/35 outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors";
const selectCls = "w-full rounded-lg bg-[#0A1128]/60 border border-white/10 px-4 py-3 text-sm text-white h-[46px] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]";

const Field = ({ label, error, children, hint, span = "" }) => (
  <div className={span}>
    <div className="flex items-center justify-between mb-1.5">
      <label className="text-[11px] uppercase tracking-[0.22em] text-white/60 font-semibold">{label}</label>
      {hint && <span className="text-[10px] text-[#D4AF37]">{hint}</span>}
    </div>
    {children}
    {error && <div className="text-[11px] text-red-400 mt-1">{error}</div>}
  </div>
);
